import React from 'react';
import { Repeat, MapPin, Bell } from 'lucide-react';
import { CalendarOccurrence } from '../../types';
import { DayItem, formatDateKo, keyParts, timeLabelForDay } from '../../utils/calendarDate';
import { SCOPE_STYLE } from './scopeStyles';

interface AgendaRowProps {
  entry: DayItem<CalendarOccurrence>;
  dayKey: string;
  onOpen: (occ: CalendarOccurrence) => void;
  compact?: boolean;
}

/** 일정 한 줄 — 시간 칸 · 분류색 띠 · 제목 · 보조 줄(장소·분류·반복·초대) */
export const AgendaRow: React.FC<AgendaRowProps> = ({ entry, dayKey, onOpen, compact }) => {
  const o = entry.item;
  const time = timeLabelForDay(o, dayKey);
  const style = o.source === 'intranet' ? SCOPE_STYLE.dept : SCOPE_STYLE[o.scope];
  const pendingInvite = o.myRole === 'attendee' && o.myResponse === 'pending';
  const labels: string[] = [];
  if (o.source === 'intranet') labels.push('인트라넷');
  else if (o.scope !== 'personal') labels.push(style.label);
  if (o.myRole === 'attendee' && o.ownerName) labels.push(`${o.ownerName} 님 일정`);
  const hasMeta = labels.length > 0 || pendingInvite || !!o.location;
  return (
    <button
      type="button"
      onClick={() => onOpen(o)}
      className={`w-full flex items-stretch gap-2.5 ${compact ? 'px-1 py-1.5' : 'px-2 py-2'} rounded-lg text-left hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600`}
    >
      <span className={`${compact ? 'w-14' : 'w-16'} shrink-0 pt-px`}>
        <span className="block text-[13px] font-semibold text-slate-700 tabular-nums">{time.main}</span>
        {time.sub && <span className="block text-[11px] text-slate-500 tabular-nums">{time.sub}</span>}
      </span>
      <span
        aria-hidden="true"
        className={`w-1 shrink-0 rounded-full ${o.source === 'intranet' ? 'bg-emerald-300' : style.dot} ${pendingInvite ? 'opacity-50' : ''}`}
      />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={`text-sm font-semibold truncate ${pendingInvite ? 'text-slate-500' : 'text-slate-800'}`}>{o.title}</span>
          {o.repeat && <Repeat size={12} className="shrink-0 text-slate-400" aria-label="반복 일정" />}
          {o.remind && <Bell size={12} className="shrink-0 text-slate-400" aria-label="알림 있음" />}
        </span>
        {hasMeta && (
          <span className="flex items-center gap-1 text-xs text-slate-500 min-w-0">
            {pendingInvite && <span className="shrink-0 font-semibold text-blue-700">초대 · 응답 전</span>}
            {labels.length > 0 && (
              <span className="truncate shrink">{pendingInvite ? '· ' : ''}{labels.join(' · ')}</span>
            )}
            {o.location && (
              <>
                {(labels.length > 0 || pendingInvite) && <span aria-hidden="true" className="shrink-0">·</span>}
                <MapPin size={11} className="shrink-0" aria-hidden="true" />
                <span className="truncate">{o.location}</span>
              </>
            )}
          </span>
        )}
      </span>
    </button>
  );
};

interface AgendaListProps {
  /** 날짜 키 순서대로 그릴 날들 */
  days: string[];
  byDay: Record<string, DayItem<CalendarOccurrence>[]>;
  holidays: Record<string, string>;
  today: string;
  onOpen: (occ: CalendarOccurrence) => void;
  /** 일정 없는 날도 머리를 그린다(선택한 날 하루 보기) */
  showEmptyDays?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  compact?: boolean;
  /** 날짜 머리 숨김(하루짜리 목록에서 위에 이미 날짜가 있을 때) */
  hideDayHeader?: boolean;
  maxItemsPerDay?: number;
  onMore?: (dayKey: string) => void;
}

/** 날짜별 묶음 목록 — 목록 보기 · 선택한 날 · 홈 카드가 공용 */
export const AgendaList: React.FC<AgendaListProps> = ({
  days, byDay, holidays, today, onOpen, showEmptyDays, emptyTitle, emptyBody, compact, hideDayHeader, maxItemsPerDay, onMore,
}) => {
  const visible = showEmptyDays ? days : days.filter(d => (byDay[d] || []).length > 0);
  if (visible.length === 0 || (showEmptyDays && visible.every(d => (byDay[d] || []).length === 0) && hideDayHeader)) {
    return (
      <div className={compact ? 'px-1 py-2' : 'px-2 py-6 text-center'}>
        <p className="text-sm font-semibold text-slate-700">{emptyTitle || '일정이 없습니다'}</p>
        {emptyBody && <p className="mt-1 text-[13px] text-slate-500">{emptyBody}</p>}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {visible.map(day => {
        const items = byDay[day] || [];
        const p = keyParts(day);
        const holiday = holidays[day];
        const shown = maxItemsPerDay ? items.slice(0, maxItemsPerDay) : items;
        return (
          <section key={day} aria-label={formatDateKo(day)} data-day={day}>
            {!hideDayHeader && (
              <h3 className={`flex items-baseline gap-1.5 px-2 pt-2 pb-1 text-[13px] font-bold ${day === today ? 'text-brand-700' : p.dow === 0 || holiday ? 'text-brand-700' : p.dow === 6 ? 'text-blue-700' : 'text-slate-600'}`}>
                {day === today ? `오늘 · ${formatDateKo(day)}` : formatDateKo(day)}
                {holiday && <span className="font-medium text-slate-500">{holiday}</span>}
              </h3>
            )}
            {items.length === 0 ? (
              <p className="px-2 pb-2 text-[13px] text-slate-500">일정이 없습니다</p>
            ) : (
              <>
                {shown.map(entry => (
                  <AgendaRow key={`${entry.item.key}@${day}`} entry={entry} dayKey={day} onOpen={onOpen} compact={compact} />
                ))}
                {maxItemsPerDay && items.length > maxItemsPerDay && (
                  <button
                    type="button"
                    onClick={() => onMore?.(day)}
                    className="ml-2 mt-0.5 px-2 py-1 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    일정 {items.length - maxItemsPerDay}개 더 보기
                  </button>
                )}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};
